/* eslint-disable */
import {
  createSlice,
  createAsyncThunk,
  createEntityAdapter,
  createSelector,
} from "@reduxjs/toolkit";
import { apiRequest } from "../../utils/api";

// --- auth header (JWT from localStorage) ---
const authHeader = () => {
  const t = localStorage.getItem("access");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

// Small helper for UI fallback
const humanize = (s = "") =>
  String(s)
    .split("_")
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : ""))
    .join(" ");

// Ensure every record has a unique, stable id + sane defaults
const normalizeTag = (t, idx) => {
  const safeId =
    t?.id ??
    (t?.code ? `code:${t.code}` : `row:${idx}`); // guarantee uniqueness if id is missing
  return {
    id: safeId,
    code: t?.code ?? `auto_${idx}`,
    name: t?.name ?? humanize(t?.code ?? ""),
    color: t?.color ?? "#1677ff",
    is_active: t?.is_active ?? true,
    sort_order: Number.isFinite(t?.sort_order) ? t.sort_order : 0,
  };
};

// --- entity adapter (store by id OR code as fallback) ---
const tagsAdapter = createEntityAdapter({
  selectId: (t) => t.id ?? t.code,
  sortComparer: (a, b) => {
    const ao = Number.isFinite(a.sort_order) ? a.sort_order : 0;
    const bo = Number.isFinite(b.sort_order) ? b.sort_order : 0;
    if (ao !== bo) return ao - bo;
    return String(a.name || "").localeCompare(String(b.name || ""));
  },
});

const initialState = tagsAdapter.getInitialState({
  loading: false,
  error: null,
  lastFetchedAt: null,
});

const STALE_MS = 5 * 60 * 1000; // 5 minutes

// ---------------------------------------------
// Fetch all (optionally include inactive)
// ---------------------------------------------
export const fetchTags = createAsyncThunk(
  "tags/fetchAll",
  async (params = {}, thunkAPI) => {
    const { includeInactive = false, ordering } = params;
    const q = new URLSearchParams();
    if (includeInactive) q.set("include_inactive", "1");
    if (ordering) q.set("ordering", ordering);
    const url = `/tags/${q.toString() ? `?${q.toString()}` : ""}`;
    const res = await apiRequest(url, { headers: { ...authHeader() } });
    if (!res.ok) {
      let msg = `HTTP ${res.status}`;
      try {
        const j = await res.json();
        if (j?.detail) msg = j.detail;
      } catch {}
      throw new Error(msg);
    }
    const data = await res.json();
    const raw = Array.isArray(data) ? data : data.results ?? [];
    return raw.map(normalizeTag);
  },
  {
    condition: (params = {}, { getState }) => {
      if (params.force) return true;
      const { tags } = getState();
      if (!tags?.lastFetchedAt) return true;
      return Date.now() - tags.lastFetchedAt > STALE_MS;
    },
  }
);

// ---------------------------------------------
// Create
// ---------------------------------------------
export const createTag = createAsyncThunk(
  "tags/create",
  async (payload, thunkAPI) => {
    const res = await apiRequest(`/tags/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...authHeader(),
      },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      let msg = `HTTP ${res.status}`;
      try {
        const j = await res.json();
        msg = j?.detail || j?.error || JSON.stringify(j) || msg;
      } catch {}
      throw new Error(msg);
    }
    const created = await res.json();
    return normalizeTag(created, 0);
  }
);

// ---------------------------------------------
// Update (PATCH)
// ---------------------------------------------
export const updateTag = createAsyncThunk(
  "tags/update",
  async ({ id, data }, thunkAPI) => {
    if (!id) throw new Error("Missing id");
    const res = await apiRequest(`/tags/${id}/`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        ...authHeader(),
      },
      body: JSON.stringify(data),
    });
    if (!res.ok) {
      let msg = `HTTP ${res.status}`;
      try {
        const j = await res.json();
        msg = j?.detail || j?.error || JSON.stringify(j) || msg;
      } catch {}
      throw new Error(msg);
    }
    const updated = await res.json();
    return normalizeTag(updated, 0);
  }
);

// ---------------------------------------------
// Delete
// ---------------------------------------------
export const deleteTag = createAsyncThunk(
  "tags/delete",
  async (id, thunkAPI) => {
    if (!id) throw new Error("Missing id");
    const res = await apiRequest(`/tags/${id}/`, {
      method: "DELETE",
      headers: { ...authHeader() },
    });
    if (!res.ok && res.status !== 204) {
      let msg = `HTTP ${res.status}`;
      try {
        const j = await res.json();
        msg = j?.detail || j?.error || JSON.stringify(j) || msg;
      } catch {}
      throw new Error(msg);
    }
    return { id };
  }
);

// ---------------------------------------------
// Slice
// ---------------------------------------------
const tags = createSlice({
  name: "tags",
  initialState,
  reducers: {
    invalidate(state) {
      state.lastFetchedAt = null;
    },
    upsertOne: tagsAdapter.upsertOne,
  },
  extraReducers: (b) => {
    // fetch
    b.addCase(fetchTags.pending, (s) => {
      s.loading = true;
      s.error = null;
    });
    b.addCase(fetchTags.fulfilled, (s, a) => {
      s.loading = false;
      tagsAdapter.setAll(s, a.payload);
      s.lastFetchedAt = Date.now();
    });
    b.addCase(fetchTags.rejected, (s, a) => {
      s.loading = false;
      s.error = a.error?.message || "Failed to load tags";
    });

    // create
    b.addCase(createTag.fulfilled, (s, a) => {
      tagsAdapter.addOne(s, a.payload);
    });

    // update
    b.addCase(updateTag.fulfilled, (s, a) => {
      tagsAdapter.upsertOne(s, a.payload);
    });

    // delete
    b.addCase(deleteTag.fulfilled, (s, a) => {
      tagsAdapter.removeOne(s, a.payload.id);
    });
  },
});

export const { invalidate, upsertOne } = tags.actions;
export default tags.reducer;

// ---------------------------------------------
// Selectors
// ---------------------------------------------
const baseSelectors = tagsAdapter.getSelectors((state) => state.tags);

export const selectAllTags = baseSelectors.selectAll;
export const selectTagById = baseSelectors.selectById;
export const selectTagIds = baseSelectors.selectIds;

// Active only
export const selectActiveTags = createSelector(selectAllTags, (all) =>
  all.filter((t) => t.is_active)
);

// Lookup by code
export const makeSelectTagByCode = (code) =>
  createSelector(selectAllTags, (all) => all.find((t) => t.code === code) || null);

// AntD-select friendly options
export const selectAllTagOptions = createSelector(selectAllTags, (all) =>
  all.map((t) => ({ value: t.code, label: t.name || humanize(t.code) }))
);
export const selectActiveTagOptions = createSelector(selectActiveTags, (list) =>
  list.map((t) => ({ value: t.code, label: t.name || humanize(t.code) }))
);

// Loading / error
export const selectTagsLoading = (state) => state.tags.loading;
export const selectTagsError = (state) => state.tags.error;
export const selectTagsLastFetchedAt = (state) => state.tags.lastFetchedAt;
